import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import PageHeader from '../components/PageHeader';
import { 
  BarChart3, 
  TrendingUp, 
  Globe, 
  Search, 
  FileText, 
  ExternalLink,
  RefreshCw,
  CheckCircle,
  AlertCircle,
  Eye,
  Users,
  Zap
} from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar
} from 'recharts';
import SEOHead from '../components/SEO/SEOHead';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Stat Card Component - Defined outside main component
const StatCard = ({ title, value, icon: Icon, trend, color = "blue" }) => (
  <Card>
    <CardContent className="pt-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500">{title}</p>
          <p className="text-2xl font-bold mt-1">{value}</p>
          {trend && (
            <p className={`text-xs mt-1 ${trend > 0 ? 'text-green-600' : 'text-red-600'}`}>
              {trend > 0 ? '+' : ''}{trend}% από χθες
            </p>
          )}
        </div>
        <div className={`p-3 rounded-full ${color === 'blue' ? 'bg-blue-100' : color === 'green' ? 'bg-green-100' : color === 'purple' ? 'bg-purple-100' : 'bg-yellow-100'}`}>
          <Icon className={`h-6 w-6 ${color === 'blue' ? 'text-blue-600' : color === 'green' ? 'text-green-600' : color === 'purple' ? 'text-purple-600' : 'text-yellow-600'}`} />
        </div>
      </div>
    </CardContent>
  </Card>
);

// SEO Health Card Component
const SEOHealthCard = ({ contentPerformance, apiUrl }) => (
  <Card>
    <CardHeader>
      <CardTitle className="flex items-center gap-2">
        <Zap className="h-5 w-5 text-yellow-500" />
        SEO Health Status
      </CardTitle>
      <CardDescription>Κατάσταση AI SEO Optimization</CardDescription>
    </CardHeader>
    <CardContent>
      <div className="space-y-4">
        <div>
          <p className="text-sm font-medium mb-2">AI Crawlers Allowed:</p>
          <div className="flex flex-wrap gap-2">
            {['GPTBot', 'Google-Extended', 'ChatGPT-User', 'PerplexityBot', 'Claude-Web'].map(crawler => (
              <Badge key={crawler} variant="secondary" className="bg-green-100 text-green-700">
                <CheckCircle className="h-3 w-3 mr-1" />
                {crawler}
              </Badge>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 mt-4">
          <div className="p-3 bg-gray-50 rounded-lg">
            <p className="text-xs text-gray-500">Organization Schema</p>
            <div className="flex items-center gap-1 mt-1">
              <CheckCircle className="h-4 w-4 text-green-500" />
              <span className="text-sm font-medium">Active</span>
            </div>
          </div>
          <div className="p-3 bg-gray-50 rounded-lg">
            <p className="text-xs text-gray-500">FAQPage Schema</p>
            <div className="flex items-center gap-1 mt-1">
              <CheckCircle className="h-4 w-4 text-green-500" />
              <span className="text-sm font-medium">5 Q&A</span>
            </div>
          </div>
          <div className="p-3 bg-gray-50 rounded-lg">
            <p className="text-xs text-gray-500">Sitemap</p>
            <div className="flex items-center gap-1 mt-1">
              <CheckCircle className="h-4 w-4 text-green-500" />
              <span className="text-sm font-medium">{contentPerformance.length} URLs</span>
            </div>
          </div>
          <div className="p-3 bg-gray-50 rounded-lg">
            <p className="text-xs text-gray-500">Robots.txt</p>
            <div className="flex items-center gap-1 mt-1">
              <CheckCircle className="h-4 w-4 text-green-500" />
              <span className="text-sm font-medium">Optimized</span>
            </div>
          </div>
        </div>

        <div className="pt-4 border-t">
          <p className="text-sm font-medium mb-2">SEO Files:</p>
          <div className="flex flex-wrap gap-2">
            <a href={`${apiUrl}/api/sitemap.xml`} target="_blank" rel="noopener noreferrer">
              <Button variant="outline" size="sm">
                <FileText className="h-4 w-4 mr-1" />
                sitemap.xml
                <ExternalLink className="h-3 w-3 ml-1" />
              </Button>
            </a>
            <a href={`${apiUrl}/api/robots.txt`} target="_blank" rel="noopener noreferrer">
              <Button variant="outline" size="sm">
                <FileText className="h-4 w-4 mr-1" />
                robots.txt
                <ExternalLink className="h-3 w-3 ml-1" />
              </Button>
            </a>
            <a href={`${apiUrl}/api/schema.json`} target="_blank" rel="noopener noreferrer">
              <Button variant="outline" size="sm">
                <FileText className="h-4 w-4 mr-1" />
                schema.json
                <ExternalLink className="h-3 w-3 ml-1" />
              </Button>
            </a>
          </div>
        </div>
      </div>
    </CardContent>
  </Card>
);

// Keywords Card Component
const KeywordsCard = ({ keywords }) => (
  <Card>
    <CardHeader>
      <CardTitle className="flex items-center gap-2">
        <Search className="h-5 w-5 text-purple-500" />
        Target Keywords
      </CardTitle>
      <CardDescription>Λέξεις-κλειδιά για AI visibility</CardDescription>
    </CardHeader>
    <CardContent>
      <div className="space-y-4">
        <div>
          <p className="text-sm font-medium mb-2">Primary:</p>
          <div className="flex flex-wrap gap-2">
            <Badge className="bg-blue-600">Radio Solutions</Badge>
            <Badge className="bg-blue-600">radiosolutionsgreece.com</Badge>
            <Badge className="bg-blue-600">ραδιοφωνικό περιεχόμενο</Badge>
          </div>
        </div>

        <div>
          <p className="text-sm font-medium mb-2">Content Sections ({keywords.total_count || 0}):</p>
          <div className="flex flex-wrap gap-1 max-h-32 overflow-y-auto">
            {(keywords.section_keywords || []).slice(0, 30).map((kw, i) => (
              <Badge key={i} variant="outline" className="text-xs">
                {kw}
              </Badge>
            ))}
          </div>
        </div>
      </div>
    </CardContent>
  </Card>
);

const SEODashboard = () => {
  const [loading, setLoading] = useState(true);
  const [trafficData, setTrafficData] = useState([]);
  const [contentPerformance, setContentPerformance] = useState([]);
  const [keywords, setKeywords] = useState({});
  const [error, setError] = useState(null);
  const [dailyStats, setDailyStats] = useState(null);
  const [weeklyStats, setWeeklyStats] = useState(null);
  const [geoStats, setGeoStats] = useState(null);
  const [analyticsAvailable, setAnalyticsAvailable] = useState(true);

  const fetchSEOData = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};

      // Fetch SEO Keywords
      const keywordsRes = await fetch(`${API_URL}/api/seo/keywords`);
      if (keywordsRes.ok) {
        setKeywords(await keywordsRes.json());
      }

      // Fetch Sitemap Data
      const sitemapRes = await fetch(`${API_URL}/api/seo/sitemap-data`);
      if (sitemapRes.ok) {
        const data = await sitemapRes.json();
        setContentPerformance(data.pages?.slice(0, 30) || []);
      }

      // Fetch Real Analytics - Daily Stats
      try {
        const dailyRes = await fetch(`${API_URL}/api/admin/analytics/daily`, { headers });
        if (dailyRes.ok) {
          const daily = await dailyRes.json();
          setDailyStats(daily);
          setAnalyticsAvailable(true);
        } else if (dailyRes.status === 503) {
          setAnalyticsAvailable(false);
        }
      } catch (e) {
        console.log('Daily analytics not available');
      }

      // Fetch Real Analytics - Weekly Stats
      try {
        const weeklyRes = await fetch(`${API_URL}/api/admin/analytics/weekly`, { headers });
        if (weeklyRes.ok) {
          const weekly = await weeklyRes.json();
          setWeeklyStats(weekly);
          
          // Transform weekly data for chart
          if (weekly.daily_breakdown) {
            const chartData = weekly.daily_breakdown.map(day => ({
              date: new Date(day.date).toLocaleDateString('el-GR', { day: '2-digit', month: '2-digit' }),
              pageviews: day.visits || 0,
              sessions: day.unique_visitors || 0,
              logins: day.logins || 0
            }));
            setTrafficData(chartData);
          }
        }
      } catch (e) {
        console.log('Weekly analytics not available');
      }

      // Fetch Geographic Stats
      try {
        const geoRes = await fetch(`${API_URL}/api/admin/analytics/geographic`, { headers });
        if (geoRes.ok) {
          const geo = await geoRes.json();
          setGeoStats(geo);
        }
      } catch (e) {
        console.log('Geographic analytics not available');
      }

      // If no real data, use mock data for demo
      if (!weeklyStats || !weeklyStats.daily_breakdown) {
        const mockData = [];
        const today = new Date();
        for (let i = 29; i >= 0; i--) {
          const date = new Date(today);
          date.setDate(date.getDate() - i);
          mockData.push({
            date: date.toLocaleDateString('el-GR', { day: '2-digit', month: '2-digit' }),
            pageviews: Math.floor(Math.random() * 500) + 100,
            sessions: Math.floor(Math.random() * 200) + 50
          });
        }
        setTrafficData(mockData);
      }

    } catch (err) {
      console.error('Error fetching SEO data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSEOData();
  }, [fetchSEOData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <RefreshCw className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-[#1A0730]">
      <PageHeader 
        title="SEO & Analytics Dashboard" 
        subtitle="AI-Optimized για ChatGPT, Gemini, Perplexity"
      />
      
      <div className="container mx-auto p-6 space-y-6">
        <SEOHead 
          title="SEO & Analytics Dashboard"
          description="Διαχείριση SEO και Analytics για Radio Solutions"
        />

        <div className="flex justify-end">
          <Button onClick={fetchSEOData} variant="outline">
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {error && (
          <div className="bg-red-50 text-red-600 p-4 rounded-lg flex items-center gap-2">
            <AlertCircle className="h-5 w-5" />
            {error}
          </div>
        )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard 
          title="Σημερινές Επισκέψεις" 
          value={dailyStats?.visits || trafficData[trafficData.length - 1]?.pageviews || 0}
          icon={Eye}
          trend={dailyStats?.trend_vs_yesterday}
          color="blue"
        />
        <StatCard 
          title="Μοναδικοί Επισκέπτες" 
          value={dailyStats?.unique_visitors || trafficData[trafficData.length - 1]?.sessions || 0}
          icon={Users}
          color="green"
        />
        <StatCard 
          title="Content Sections" 
          value={contentPerformance.length}
          icon={FileText}
          color="purple"
        />
        <StatCard 
          title="SEO Score" 
          value={analyticsAvailable ? "95/100" : "N/A"}
          icon={TrendingUp}
          color="yellow"
        />
      </div>

      {!analyticsAvailable && (
        <div className="bg-yellow-50 text-yellow-700 p-4 rounded-lg flex items-center gap-2">
          <AlertCircle className="h-5 w-5" />
          Analytics tracking δεν είναι ενεργοποιημένο. Τα δεδομένα είναι demo.
        </div>
      )}

      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="traffic">Traffic</TabsTrigger>
          <TabsTrigger value="seo">SEO Tools</TabsTrigger>
          <TabsTrigger value="content">Content</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <SEOHealthCard contentPerformance={contentPerformance} apiUrl={API_URL} />
            <KeywordsCard keywords={keywords} />
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Traffic Overview (30 Days)</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={trafficData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip />
                    <Line type="monotone" dataKey="pageviews" stroke="#3b82f6" strokeWidth={2} name="Page Views" />
                    <Line type="monotone" dataKey="sessions" stroke="#10b981" strokeWidth={2} name="Sessions" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="traffic" className="space-y-4">
          {/* Weekly Summary Stats */}
          {weeklyStats && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card className="bg-blue-50 dark:bg-blue-900/20">
                <CardContent className="p-4">
                  <div className="text-2xl font-bold text-blue-600">{weeklyStats.total_visits || 0}</div>
                  <div className="text-sm text-blue-600/80">Total Weekly Visits</div>
                </CardContent>
              </Card>
              <Card className="bg-green-50 dark:bg-green-900/20">
                <CardContent className="p-4">
                  <div className="text-2xl font-bold text-green-600">{weeklyStats.unique_visitors || 0}</div>
                  <div className="text-sm text-green-600/80">Unique Visitors</div>
                </CardContent>
              </Card>
              <Card className="bg-purple-50 dark:bg-purple-900/20">
                <CardContent className="p-4">
                  <div className="text-2xl font-bold text-purple-600">{dailyStats?.total_logins || 0}</div>
                  <div className="text-sm text-purple-600/80">Today's Logins</div>
                </CardContent>
              </Card>
              <Card className="bg-orange-50 dark:bg-orange-900/20">
                <CardContent className="p-4">
                  <div className="text-2xl font-bold text-orange-600">
                    {weeklyStats.daily_average ? Math.round(weeklyStats.daily_average) : 0}
                  </div>
                  <div className="text-sm text-orange-600/80">Avg Daily Visits</div>
                </CardContent>
              </Card>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Daily Traffic</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={trafficData.slice(-14)}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                      <YAxis tick={{ fontSize: 10 }} />
                      <Tooltip />
                      <Bar dataKey="pageviews" fill="#3b82f6" name="Page Views" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Top Pages</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {contentPerformance.slice(0, 10).map((page, i) => (
                    <div key={i} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                      <span className="text-sm truncate max-w-xs">{page.title || page.url}</span>
                      <Badge variant="secondary">{page.priority}</Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Geographic Statistics */}
          {geoStats && geoStats.top_countries && geoStats.top_countries.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Globe className="h-5 w-5" />
                  Geographic Distribution
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Countries */}
                  <div>
                    <h4 className="font-semibold mb-3">Top Countries</h4>
                    <div className="space-y-2">
                      {geoStats.top_countries.slice(0, 10).map((country, i) => (
                        <div key={i} className="flex justify-between items-center p-2 bg-gray-50 dark:bg-gray-800 rounded">
                          <span className="flex items-center gap-2">
                            <span className="text-lg">{country.flag || '🌍'}</span>
                            <span className="text-sm">{country.country}</span>
                          </span>
                          <Badge variant="secondary">{country.visits} visits</Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  {/* Cities */}
                  <div>
                    <h4 className="font-semibold mb-3">Top Cities</h4>
                    <div className="space-y-2">
                      {geoStats.top_cities && geoStats.top_cities.slice(0, 10).map((city, i) => (
                        <div key={i} className="flex justify-between items-center p-2 bg-gray-50 dark:bg-gray-800 rounded">
                          <span className="text-sm">{city.city}</span>
                          <Badge variant="secondary">{city.visits} visits</Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="seo" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Globe className="h-5 w-5" />
                  Sitemap
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    <span>Auto-generated daily</span>
                  </div>
                  <div className="text-sm text-gray-500">
                    {contentPerformance.length} URLs indexed
                  </div>
                  <a href={`${API_URL}/api/sitemap.xml`} target="_blank" rel="noopener noreferrer">
                    <Button className="w-full" variant="outline">
                      View Sitemap
                      <ExternalLink className="h-4 w-4 ml-2" />
                    </Button>
                  </a>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="h-5 w-5" />
                  Structured Data
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    <span>Organization</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    <span>WebSite</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    <span>FAQPage (5 items)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    <span>SoftwareApplication</span>
                  </div>
                  <a href={`${API_URL}/api/schema.json`} target="_blank" rel="noopener noreferrer">
                    <Button className="w-full mt-2" variant="outline">
                      View Schema
                      <ExternalLink className="h-4 w-4 ml-2" />
                    </Button>
                  </a>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Robots.txt
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    <span>AI Crawlers Allowed</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    <span>Sitemap Reference</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    <span>Admin Protected</span>
                  </div>
                  <a href={`${API_URL}/api/robots.txt`} target="_blank" rel="noopener noreferrer">
                    <Button className="w-full mt-2" variant="outline">
                      View Robots.txt
                      <ExternalLink className="h-4 w-4 ml-2" />
                    </Button>
                  </a>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="content" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Content Sections</CardTitle>
              <CardDescription>Ενότητες περιεχομένου με SEO metadata</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 px-4">Title</th>
                      <th className="text-left py-2 px-4">URL</th>
                      <th className="text-left py-2 px-4">Priority</th>
                      <th className="text-left py-2 px-4">Update Freq</th>
                    </tr>
                  </thead>
                  <tbody>
                    {contentPerformance.map((page, i) => (
                      <tr key={i} className="border-b hover:bg-gray-50">
                        <td className="py-2 px-4 font-medium">{page.title}</td>
                        <td className="py-2 px-4 text-sm text-gray-500">{page.url}</td>
                        <td className="py-2 px-4">
                          <Badge variant={page.priority >= 0.8 ? 'default' : 'secondary'}>
                            {page.priority}
                          </Badge>
                        </td>
                        <td className="py-2 px-4 text-sm">{page.changefreq || 'daily'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
      </div>
    </div>
  );
};

export default SEODashboard;
